import { Injectable } from '@angular/core';
import { SyncAction } from '../../dto/sync-actions.enum';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { AuthService } from '../auth-service.service';
import { OrderChangesService } from '../order/order-changes.service';
import { BaseCacheService } from '../base-cache.service';
import { OfflineModeService } from '../offline-mode.service';
import { ModalController } from '@ionic/angular';

import { OrderApiService } from '../order/order-api.service';
import * as Sentry from '@sentry/angular';
import {
  Order,
  SignEndOfDayRequest,
  SignExpeditorNoteResultDto
} from '../../dto/order.dto';

/** Одно отложенное действие в очереди синхронизации. */
interface SyncActionRecord {
  /** Стабильный идентификатор действия — по нему удаляем из очереди, а не по индексу. */
  id?: string;
  syncActionsType: SyncAction;
  actionTargetValue: unknown;
}

/** Запись таблицы очереди для одного пользователя. */
interface SyncActionsTableRecord {
  user: string;
  syncActions: SyncActionRecord[];
}

/** Полезная нагрузка действия подписи конца дня. */
interface SignDayTarget {
  payload: SignEndOfDayRequest;
  bargainId: number;
  dayId: string;
  order: Partial<Order>;
}

/**
 * Обработка очереди отложенных действий пользователя ({@code SyncActionsTable}) при выходе в онлайн.
 * В очередь ставятся только подпись конца дня ({@code signDay}) и завершение заявки ({@code endOrder}) —
 * операции, которые нельзя терять и нельзя дублировать.
 *
 * Ключевые свойства обработки:
 * - ПОСЛЕДОВАТЕЛЬНОСТЬ: действия выполняются по одному, без наложения запросов;
 * - удаление из очереди по СТАБИЛЬНОМУ id (uuid), а не по индексу — индекс «съезжает» при правках;
 * - все изменения таблицы (постановка, бэкфилл id, удаление) идут через единый сериализатор
 *   ({@link #mutateTable}), исключающий гонку чтение-изменение-запись в IndexedDB;
 * - политика «повторять всегда, не терять»: запись удаляется ТОЛЬКО при подтверждённом успехе, любой
 *   сбой оставляет её в очереди до следующего онлайна;
 * - идемпотентность на сервере (стабильный ключ подписи, приём 208 как успех) исключает дубль при
 *   потерянном ответе — см. комментарии к соответствующим методам ниже.
 */
@Injectable({ providedIn: 'root' })
export class BackgroundSyncService extends BaseCacheService {
  private static readonly TABLE = 'SyncActionsTable';

  /** Число попыток чтения записи очереди при транзиторном сбое IndexedDB перед тем, как признать сбой. */
  private static readonly READ_RETRIES = 3;

  /** Пауза между повторами чтения — чтобы транзиторный сбой (конфликт транзакции, смена версии) успел
   * рассосаться; непосредственные повторы подряд могли бы не перекрыть кратковременную блокировку. */
  private static readonly READ_RETRY_DELAY_MS = 50;

  /**
   * Защита от повторного входа. runAllActions вызывается из нескольких мест (запуск приложения,
   * восстановление сети, обновление списка), и параллельные прогоны отправляли бы одно и то же
   * действие дважды.
   */
  private isRunning = false;

  /**
   * Сериализатор изменений таблицы очереди. И постановка действия, и удаление проходят через эту
   * цепочку промисов, поэтому конкурентные read-modify-write не затирают друг друга: в IndexedDB
   * без явных транзакций последовательное применение — единственный безопасный порядок.
   */
  private tableMutationChain: Promise<void> = Promise.resolve();

  constructor(
    dbService: NgxIndexedDBService,
    offlineService: OfflineModeService,
    authService: AuthService,
    modalCtrl: ModalController,
    private orderChangesService: OrderChangesService,
    private orderApiService: OrderApiService
  ) {
    super(dbService, offlineService, authService, modalCtrl);
  }

  /**
   * Прогоняет очередь отложенных действий. Повторные вызовы во время активного прогона игнорируются
   * (защита от повторного входа). Ошибки прогона не пробрасываются — очередь переживает сбой и будет
   * повторена при следующем выходе в онлайн.
   */
  public runAllActions(): void {
    if (this.isRunning) {
      return;
    }
    const email = this.authService.loadUserProfile().email;
    if (!email) {
      return;
    }
    this.isRunning = true;
    this.processQueue(email)
      .catch(error => Sentry.captureException(error))
      .finally(() => {
        this.isRunning = false;
      });
  }

  /**
   * Ставит действие в очередь. Через сериализатор изменений таблицы, чтобы не конкурировать с
   * удалением из очереди во время прогона.
   */
  public addSyncAction(value: unknown, action: SyncAction): void {
    const email = this.authService.loadUserProfile().email;
    if (!email) {
      return;
    }
    const newAction: SyncActionRecord = {
      id: uuid(),
      syncActionsType: action,
      actionTargetValue: value
    };
    void this.mutateTable(email, current => {
      if (current) {
        current.syncActions.push(newAction);
        return current;
      }
      return { user: email, syncActions: [newAction] };
    });
  }

  private async processQueue(email: string): Promise<void> {
    const record = await this.readTable(email);
    const actions = record?.syncActions ?? [];
    if (!actions.length) {
      return;
    }

    // Бэкфилл идентификаторов: записи, поставленные предыдущей версией приложения, не имеют id.
    // Присваиваем стабильный id и сохраняем один раз, чтобы удалять действия по идентичности.
    if (actions.some(a => !a.id)) {
      await this.mutateTable(email, current => {
        current?.syncActions.forEach(a => {
          if (!a.id) {
            a.id = uuid();
          }
        });
        return current;
      });
    }

    // Свежий снимок уже с идентификаторами.
    const snapshot = await this.readTable(email);
    const pending = snapshot?.syncActions ?? [];

    // Последовательная обработка: одно действие за другим, без наложения запросов.
    for (const action of pending) {
      const done = await this.processAction(action);
      if (done && action.id) {
        await this.removeAction(email, action.id);
      }
    }
  }

  /**
   * Выполняет одно действие. Возвращает true только при подтверждённом успехе (действие можно
   * удалить из очереди). При любом сбое — false: действие остаётся и будет повторено при следующем
   * выходе в онлайн (политика «повторять всегда, не терять»).
   */
  private async processAction(action: SyncActionRecord): Promise<boolean> {
    try {
      switch (action.syncActionsType) {
        case SyncAction.endOrder:
          return await this.processEndOrder(action.actionTargetValue as number);
        case SyncAction.signDay:
          return await this.processSignDay(
            action.actionTargetValue as SignDayTarget
          );
        default:
          // Сюда попадают только действия, которые очередь не умеет выполнять. В очередь ставятся
          // (addSyncAction) исключительно signDay и endOrder; startDay/updateRTS из enum SyncAction
          // нигде не ставятся. Значит default — это порча записи или тип из несовместимой версии.
          // Не зацикливаем повтор (обработчика нет — застрял бы навсегда): фиксируем в Sentry и
          // удаляем, чтобы отбрасывание не было молчаливым.
          Sentry.captureMessage(
            `background-sync: необрабатываемый тип действия «${action.syncActionsType}»`,
            'warning'
          );
          return true;
      }
    } catch (error) {
      Sentry.captureException(error);
      return false;
    }
  }

  private async processEndOrder(requestId: number): Promise<boolean> {
    const response = await firstValueFrom(
      this.orderApiService.finishForwardingForSync(requestId).pipe(take(1))
    );
    // Любой 2xx (в т.ч. 208 ALREADY_REPORTED при повторе) означает, что заявка завершена.
    return response.status >= 200 && response.status < 300;
  }

  private async processSignDay(target: SignDayTarget): Promise<boolean> {
    const { payload, bargainId, dayId, order: orderData } = target;
    const res: SignExpeditorNoteResultDto = await firstValueFrom(
      this.orderApiService.signExpeditorNote(payload, bargainId).pipe(take(1))
    );
    if (res.success && res.data) {
      const updatedOrder = new Order(orderData).setForwarderNoteId(
        dayId,
        res.data
      );
      this.orderChangesService.updateRequests(updatedOrder, 'archived-requests');
      return true;
    }
    // Сервер вернул логический неуспех — оставляем в очереди для повтора.
    return false;
  }

  private removeAction(email: string, actionId: string): Promise<void> {
    return this.mutateTable(email, current => {
      if (!current) {
        return null;
      }
      current.syncActions = current.syncActions.filter(a => a.id !== actionId);
      return current;
    });
  }

  /**
   * Читает запись очереди пользователя. Различает «записи нет» и «сбой чтения»: {@code null} возвращаем
   * ТОЛЬКО при подлинном отсутствии (getByKey отдал undefined). При сбое чтения повторяем несколько раз
   * (транзиторные сбои IndexedDB обычно кратковременны), и лишь исчерпав попытки — ПРОБРАСЫВАЕМ ошибку.
   * Прежняя версия глотала сбой в {@code null}, из-за чего mutateTable считал существующую запись
   * отсутствующей и добавлял её через add по существующему ключу (ConstraintError) — новое действие
   * (например, офлайн-подпись) молча терялось.
   */
  private async readTable(
    email: string
  ): Promise<SyncActionsTableRecord | null> {
    let lastError: unknown;
    for (let attempt = 0; attempt < BackgroundSyncService.READ_RETRIES; attempt++) {
      try {
        const record = await firstValueFrom(
          this.dbService.getByKey<SyncActionsTableRecord>(
            BackgroundSyncService.TABLE,
            email
          )
        );
        return record ?? null;
      } catch (error) {
        lastError = error;
        if (attempt < BackgroundSyncService.READ_RETRIES - 1) {
          await new Promise<void>(resolve =>
            setTimeout(resolve, BackgroundSyncService.READ_RETRY_DELAY_MS)
          );
        }
      }
    }
    throw lastError;
  }

  /**
   * Сериализованное изменение записи очереди. mutator получает текущую запись (или null, если её ещё
   * нет) и возвращает новую запись для сохранения либо null — чтобы ничего не менять. Все изменения
   * таблицы (постановка, бэкфилл, удаление) идут через эту цепочку, исключая гонки read-modify-write.
   */
  private mutateTable(
    email: string,
    mutator: (
      current: SyncActionsTableRecord | null
    ) => SyncActionsTableRecord | null
  ): Promise<void> {
    const next = this.tableMutationChain
      .then(async () => {
        const current = await this.readTable(email);
        const result = mutator(current);
        if (!result) {
          return;
        }
        // Единый update (= objectStore.put, upsert) и для новой записи, и для существующей. Отдельная
        // ветка add убрана: под ложным null от сбоя чтения она делала add по существующему ключу
        // (ConstraintError → потеря нового действия). Теперь readTable надёжен (повторяет при сбое и
        // пробрасывает, а не выдаёт ложный null), поэтому current достоверен и put безопасен: при
        // подлинном отсутствии записи put вставит её, при наличии — перезапишет объединённую.
        await firstValueFrom(
          this.dbService.update(BackgroundSyncService.TABLE, result)
        );
      })
      .catch(error => {
        Sentry.captureException(error);
      });
    this.tableMutationChain = next;
    return next;
  }
}
